import * as React from 'react'
import ArrowBackIos from '@mui/icons-material/ArrowBackIos'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Link from '@mui/material/Link'
import { type SnackbarOrigin } from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'
import { useAuth } from 'react-oidc-context'
import { connect, useSelector, type ConnectedProps } from 'react-redux'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import SearchBox from '../../../../components/SearchBox'
import LanguageStrings from '../../../../i18n/locales'
import { type RootState } from '../../../../store'
import { getOperationPermissions } from '../../../dashboard/redux/actionCreator'
import { type OperationPermissionState } from '../../../dashboard/redux/operationPermissionSlice'
import { fetchDeviceList, fetchGatewayInfo } from '../../redux/actionCreators'
import { type DeviceListState } from '../../redux/deviceListSlice'
import { type GatewayInfoState } from '../../redux/gatewayInfoSlice'
import { Button } from '../../../../components/Button'
import { downloadImage } from '../../../device-management/redux/actionCreators'
import type { DownloadImageState } from '../../../device-management/redux/downloadImageSlice'
import AddAssetDummy from '../../../../assets/AddDevice/image.png'

import EnhancedTable, { type Sort } from './DeviceTable'
import GatewayInformation from '../GatewayInformation'

export interface State extends SnackbarOrigin {
  open: boolean
}

const DeviceListStrings = LanguageStrings().DeviceManagementCollectionStrings.DeviceListComponent
const DashboardStrings = LanguageStrings().Dashboard

// these values are hardcoded because the hierarchical link is not implemented based on priority. And, that they will be fetched from backend once the links are established
const assetTypeId = 'fdd42da4-4a13-4f57-9e1a-208d95c7f212'

const GatewayInfoStyle: React.CSSProperties = {
  width: '99%',
  color: '#F0F0F0',
  padding: '1em',
  backgroundColor: '#272727',
  borderRadius: '5px',
  fontWeight: '500',
  marginBottom: '25px'
}
const style: React.CSSProperties = {
  width: '99%',
  backgroundColor: '#272727',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  marginBottom: '16px',
  padding: 0,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0
}
const searchBoxComponent: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between'
}

function DeviceList (props: PropsFromRedux): JSX.Element {
  const auth = useAuth()
  const navigate = useNavigate()
  const stateLocation = useLocation()
  const { parentAssetId } = useParams() as { parentAssetId: string }
  const [imageBinaryData, setImageBinaryData] = React.useState<any>(AddAssetDummy)
  const [searchValue, setSearchValue] = React.useState('')
  const rowsPerPage = 10
  const [page, setPage] = React.useState<number>(1)
  const [sort, setSort] = React.useState<Sort>({ order: 'asc', orderBy: '' })
  const operationPermissions: OperationPermissionState = useSelector((state: RootState) => state.operationPermissions)
  const tcpProtocolName = 'MODBUS-TCP/IP'

  interface StateLocation {
    parentSiteId: string
    deviceAssetId: string
  }
  const navigateState = stateLocation.state as StateLocation | null
  /* istanbul ignore next */
  const parentSiteId = (navigateState != null) ? navigateState.parentSiteId : ''
  /* istanbul ignore next */
  const deviceAssetId = (navigateState != null) ? navigateState.deviceAssetId : ''
  const isTcpProtocol = props?.gatewayInfo?.gatewayInfo?.communication_protocol_name === tcpProtocolName
  const communicationProtocol = props?.gatewayInfo?.gatewayInfo?.communication_protocol_id

  /* istanbul ignore next */
  const handleSearch = (): void => {
    setPage(1)
    props.fetchDeviceList(parentSiteId, assetTypeId, page, rowsPerPage, searchValue, sort.order, deviceAssetId, sort.orderBy)
  }

  function handleBreadCrumbsClick (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    event.preventDefault()
  }

  React.useEffect(() => {
    /* istanbul ignore else */
    if (!operationPermissions.updated) {
      props.getOperationPermissions(auth.user?.profile.sub as string)
    }
    props.fetchGatewayInfo(parentAssetId, deviceAssetId)
    props.downloadImage(parentAssetId, 'fdd42da4-4a13-4f57-9e1a-208d95c7f213', '')
  }, [])

  React.useEffect(() => {
    /* istanbul ignore else */
    if (props?.downloadImageState?.downloadImage?.binaryData !== '') {
      setImageBinaryData(props?.downloadImageState?.downloadImage)
    }
  }, [props?.downloadImageState?.downloadImage?.binaryData])

  return (
    <div>
      <div role='presentation' data-testid='breadcrumbs' onClick={handleBreadCrumbsClick} className='AmiHeading' >
        <ArrowBackIos className="assetmgmtli" data-testid="breadcrumb-arrow-back" aria-label="navArrow" sx={{ marginTop: '10px' }}
          onClick={() => { navigate(-1) }}
        />
        <Breadcrumbs aria-label='breadcrumb' sx={{ color: '#f0f0f0', fontSize: '12px', marginBottom: '14px', fontFamily: 'Honeywell Sans Web', marginTop: '12px' }}>
          <Link aria-label='navBreadcrumb1' className='assetmgmtli' data-testid="bc-link-1" underline='none' color='inherit' onClick={() => { navigate('/thermal-dashboard') }} >
            {DashboardStrings.HOME}
          </Link>
          <Link aria-label='navBreadcrumb2' className='assetmgmtli' data-testid="bc-link-2" underline='none' color='inherit' onClick={() => { navigate('/site-list') }} >
            {DashboardStrings.SiteManagement}
          </Link>
          <Link aria-label='navBreadcrumb3' className='assetmgmtli' data-testid="bc-link-3" underline='none' color='inherit'
            onClick={() => { navigate(`/thermal-asset-list/${parentSiteId}`) }} >
            {DashboardStrings.AssetManagement}
          </Link>
          <Link aria-label="navBreadcrumb4" className='assetmgmtli' data-testid="bc-link-4" underline="none" color="inherit"
            onClick={() => { navigate(`/gateway-list/${deviceAssetId}`, { state: { parentSiteId } }) }} >
            {DashboardStrings.GatewayManagement}
          </Link>
          <Typography color='inherit' sx={{ fontSize: '12px', fontFamily: 'Honeywell Sans Web' }}>{DashboardStrings.DeviceManagement}</Typography>
        </Breadcrumbs>
      </div>
      <div className='ArrowDeviceList'>
        <div>
          <span data-testid='deviceHistoryDetails' className='deviceMgtDevicelist' style={{ fontWeight: '700' }}>{DeviceListStrings.Device}</span>
        </div>
      </div>
      <div style={GatewayInfoStyle}>
        <GatewayInformation parentSiteId={parentSiteId} deviceAssetId={deviceAssetId} gatewayInfo={props.gatewayInfo} imageBinaryData={imageBinaryData} />
      </div>
      <div className='configurations-table-heading'>
        <h5>{DeviceListStrings.DeviceList}</h5>
      </div>
      <div style={style}>
        <>
          <div style={searchBoxComponent}>
            <SearchBox
              sx={{ width: '384px' }}
              placeholder={DeviceListStrings.Search}
              immediate={true}
              emptyEnterFallback={() => { props.fetchDeviceList(parentSiteId, assetTypeId, page, rowsPerPage, searchValue, sort.order, deviceAssetId, sort.orderBy) }}
              searchVal={searchValue}
              setSearchVal={setSearchValue}
              performAction={handleSearch} />
            <span style={{ margin: '10px 17px' }}>
              {
                operationPermissions.permissions.can_add_tiq_assets
                  ? <span id='top-right-btn-span'>
                      <Button sx={{ margin: 0, padding: '6px 20px' }} onClick={() => { navigate(`/create-device/${parentAssetId}`, { state: { isTcpProtocol, parentSiteId, deviceAssetId, communicationProtocol } }) }} width='fit-content' dataTestId='create-device-btn'>{DeviceListStrings.AddNewDevice}</Button>
                    </span>
                  : <></>
              }
            </span>
          </div>
          <EnhancedTable
            isTcpProtocol={isTcpProtocol}
            assetTypeId={assetTypeId}
            searchVal={searchValue}
            parentAssetId={parentAssetId}
            parentSiteId={parentSiteId}
            deviceAssetId={deviceAssetId}
            page={page}
            rowsPerPage={rowsPerPage}
            sort={sort}
            setPage={setPage}
            setSort={setSort}
            performAction={() => { props.fetchDeviceList(parentSiteId, assetTypeId, page, rowsPerPage, searchValue, sort.order, deviceAssetId, sort.orderBy) }} />
        </>
      </div>
    </div>
  )
}

interface DispatchToProps {
  fetchDeviceList: (
    parentSiteId: string,
    assetTypeId: string,
    PageNumber: number,
    PageItems: number,
    searchKey: string,
    SortParams: string,
    parentAssetId: string,
    sortField: string
  ) => void
  getOperationPermissions: (sub: string) => void
  fetchGatewayInfo: (parentAssetId: string, assetId: string) => void
  downloadImage: (assetId: string, assetTypeId: string, locale: string) => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchDeviceList: (
    parentSiteId: string,
    assetTypeId: string,
    PageNumber: number,
    PageItems: number,
    searchKey: string,
    SortParams: string,
    parentAssetId: string,
    sortField: string
  ) => dispatch(fetchDeviceList(parentSiteId, assetTypeId, PageNumber, PageItems, searchKey, SortParams, parentAssetId, sortField)),
  downloadImage: (assetId: string, assetTypeId: string, locale: string) => dispatch(downloadImage(assetId, assetTypeId, locale)),
  getOperationPermissions: (sub: string) => dispatch(getOperationPermissions(sub)),
  fetchGatewayInfo: (parentAssetId: string, assetId: string) => dispatch(fetchGatewayInfo(parentAssetId, assetId))
})

interface StateToProps {
  deviceList: DeviceListState
  grandParentBreadcrumb: string
  downloadImageState: DownloadImageState
  gatewayInfo: GatewayInfoState
}
const mapStateToProps = (state: RootState): StateToProps => ({
  deviceList: state.deviceManagementDeviceList,
  gatewayInfo: state.deviceManagementGatewayInfo,
  grandParentBreadcrumb: state.grandParentBreadcrumb,
  downloadImageState: state.downloadImageState
})
const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(DeviceList)
